import { tWithKeyAttr as t } from "../../i18nHelper";
import {
  IAppointmentsProgramsDataWithLabels,
  IAppointmentsProgramsMetaData,
  ITherapistDocument,
  TQuestionnaireButton,
  TNamedItem,
  TCompanyService,
  TTreatmentRoom,
} from "../../types/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authApi } from "../api/auth.api";
import { IRole, RootState } from "../types";
import { IAuthResponse, IUserAuth, IUserResponse } from "../types/auth.types";
import { deleteCookie, getCookie, setCookie } from "../../utils/token";
import { userApi } from "../api/user.api";
import {
  getMainPageTexts,
  therapistPOCQuestionnaireName,
} from "../../utils/constants";
import {
  ICompanyDetails,
  TGetAllTherapistUsersResponse,
  TGetStatusesResponse,
  TMainPageInfo,
  TMultyCompanyDetails,
  TQuestionnaireGroup,
  TRRMListEntry,
  TSpecialization,
  TTherapistSpecialization,
  TTherapistState,
  TUnSeenAppointmentsProgram,
  TPendingReport,
  IPendingReportResponce,
  TGenderResponse,
  TMediaResponse,
  TMigzarResponse,
} from "../types/user.types";
import { inquiries } from "../../utils/inquirues";
import {
  serverStatusesDefault,
  therapistDocuments,
} from "../../utils/therapistDocuments";
import { groupByFakeCompanyId } from "../../utils/utils";

const accessToken = getCookie("accessToken");
const refreshToken = getCookie("refreshToken");

const initialUser: TTherapistState = {
  id: "",
  roleId: 0,
  MigzarId: 0,
  GenderId: 0,
  firstName: "",
  lastName: "",
  fullName: "",
  account: "",
  avatar: "",
  email: "",
  password: "",
  phone: "",
  address: "",
  unreadMessages: 0,
  availability: [],
  companyId: 0,
  degree: "פסיכיאטר",
  about: t('PROFILE_NO_INFO'),
  allowPatientBooking: true,
  mainPageUpdates: getMainPageTexts(),
  inquiries: inquiries,
  users: [],
  documents: therapistDocuments,
  therapistSpecializations: [],
  allSpecializations: [],
  actionReportQuestions: [],
  treatmentPlan: [],
  vetek: null,
};

const appointmentsProgramsDataInitialState: IAppointmentsProgramsDataWithLabels =
  {
    Medias: [],
    Roles: [],
    Periods: [],
    AppointmentsDurations: [],
    AppointmentsSequence: [],
    Genders: [],
    Migzars: [],
    AppointmentsProgramsStatus: [],
    TherapistsSpecializations: [],
    FundingSources: [],
    TreatmentTypes: [],
    TreatmentRoomsList: [],
    CompanyServicesList: [],
  };

const initialState: IUserAuth = {
  user: initialUser,
  currentUser: null,
  scroll: { currentInquiryId: null, currentTreatmentPlanId: null },
  therapistWithUsers: null,
  usersWithOutTherapist: null,
  usersHosenSderot: null,
  groupedUsersWithoutTherapistByFakeCompanyId: {
    null: [],
  },
  showUserJoinedModal: null,
  statuses: serverStatusesDefault,
  migzars: [],
  genders: [],
  medias: [],
  roles: [],
  companies: [],
  selectedCompany: null,
  isChargingCustomers: false,
  therapistsCanSeePrices: false,
  canAddDefaultReports: false,
  accessToken: accessToken,
  refreshToken: refreshToken,
  error: null,
  isAuth: false,
  calendarAppointments: [],
  allTreatmentPlan: [],
  RRMList: [],
  questionnairWithoutUserButtons: [],
  questionnairWithUserButtons: [],
  userQuestionnairSiteLink: null,
  unSeenAppointmentsPrograms: 0,
  pendingReportAppointments: [],
  pendingReportInquiries: [],
  totalPendingReports: 0,
  appointmentsProgramsData: appointmentsProgramsDataInitialState,
  isRecordingInProgress: false,
};

const storeUser = (state: IUserAuth, payload: IUserResponse) => {
  for (let key in payload.user) {
    state.user[key.charAt(0).toLowerCase() + key.slice(1)] = payload.user[key];
    state.isMultiCompany = payload?.isMultiCompany;
  }
};

const storeTokens = (
  state: IUserAuth,
  { payload }: { payload: IAuthResponse }
) => {
  state.error = payload.success ? null : payload.message;
  const { accessToken } = payload;
  if (!accessToken) {
    return;
  }
  state.isAuth = true;
  state.accessToken = accessToken;

  // state.refreshToken = refreshToken;
  if (state.accessToken) {
    deleteCookie("accessToken");
    setCookie("accessToken", state.accessToken, {
      //cookie expire on 12am.
      expires: (() => {
        const currentTime = new Date();
        const nextDay12AM = new Date(currentTime);
        nextDay12AM.setDate(currentTime.getDate() + 1);
        nextDay12AM.setHours(0, 0, 0, 0);
        //set cookie multiply by 1000 hence we divide by 1000.
        return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
      })(),
    });
  }
  // if (state.refreshToken) {
  //   setCookie("refreshToken", state.refreshToken);
  // }
};
const login = (state: IUserAuth, payload: { payload: IAuthResponse }) => {
  storeTokens(state, payload);
  const { user } = payload.payload;
  if (user) {
    //@ts-ignore
    storeUser(state, payload.payload);
  }
};
const storeErrors = (state: IUserAuth, { payload }: any) => {
  const { data } = payload;
  state.error =
    typeof data?.message === "string"
      ? data?.message
      : t('MAIN_PAGE_TREATMENT_ERROR');
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setPhone: (state, { payload }) => {
      return { ...state, user: { ...state.user, phone: payload } };
    },
    // setId: (state, { payload }) => {
    //   return { ...state, user: { ...state.user, id: payload } };
    // },
    setRoleId: (state, { payload }) => {
      return { ...state, user: { ...state.user, roleId: payload } };
    },
    setLoginInfo: (
      state,
      {
        payload,
      }: { payload: { email: string; password: string; id: number | string } }
    ) => {
      return {
        ...state,
        user: {
          ...state.user,
          email: payload.email,
          password: payload.password,
          id: payload.id,
        },
      };
    },
    setCurrentInquiry: (state, { payload }) => {
      return {
        ...state,
        scroll: {
          currentInquiryId: payload,
          currentTreatmentPlanId: null,
        },
      };
    },
    setCurrentTreatmentPlan: (state, { payload }) => {
      return {
        ...state,
        scroll: {
          currentTreatmentPlanId: payload,
          currentInquiryId: null,
        },
      };
    },
    // updateStateAppointment: (state, { payload }) => {
    //   const appointment = state.user.appointments.find(
    //     (a: IAppointment) => a.id === payload.appointmentId
    //   );
    //   if (appointment) appointment.date = payload.date;
    // },
    // cancelAppointment: (state, { payload }) => {
    //   return {
    //     ...state,
    //     user: {
    //       ...state.user,
    //       appointments: state.user.appointments.filter(
    //         (a: IAppointment) => a.id !== payload.id
    //       ),
    //     },
    //   };
    // },
    setAvailability: (state, { payload }) => {
      return { ...state, availability: payload };
    },
    setShowUserJoinedModal: (state, { payload }: { payload: number }) => {
      return {
        ...state,
        showUserJoinedModal: payload,
      };
    },
    setSelectedCompany: (state, { payload }: { payload: TMultyCompanyDetails | null }) => {
      return {
        ...state,
        selectedCompany: payload,
        user: {
          ...state.user,
          companyId: payload?.CompanyId ?? 0,
        },
      };
    },
    setUserAvatar: (state, { payload }: { payload: string | null }) => {
      state.user.avatar = payload ?? "";
    },
    resetShowUserJoinedModal: (state) => {
      return {
        ...state,
        showUserJoinedModal: null,
      };
    },
    logout: () => {
      deleteCookie("accessToken");
      return { ...initialState };
    },
    setSendWSMessage: (state: IUserAuth, { payload }) => {
      return {
        ...state,
        sendWSMessage: payload,
      };
    },
    updateDocumentStatus: (state, { payload }) => {
      return {
        ...state,
        user: {
          ...state.user,
          documents: state.user.documents.map((doc: ITherapistDocument) =>
            doc.id === payload.id ? { ...doc, status: payload.status } : doc
          ),
        },
      };
    },
    clearRetryAfter: (state) => {
      return {
        ...state,
        retryAfterTimestamp: null,
      };
    },
    setRecordingInProgress: (state, { payload }: { payload: boolean }) => {
      state.isRecordingInProgress = payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, login)
      .addMatcher(authApi.endpoints.verifySsoToken.matchFulfilled, login)
      .addMatcher(authApi.endpoints.redeemSwitchCode.matchFulfilled, login)
      .addMatcher(
        authApi.endpoints.login.matchRejected,
        (state: IUserAuth, { payload }: any) => {
          const { data, status } = payload;
          if (status === 429) {
            state.error = "יותר מדי ניסיונות התחברות. אנא נסו שוב מאוחר יותר.";
            state.retryAfterTimestamp = Date.now() + 60 * 1000;
          } else {
            state.error =
              typeof data?.message === "string"
                ? data?.message
                : t('MAIN_PAGE_TREATMENT_ERROR');
          }
        }
      )
      .addMatcher(authApi.endpoints.loginGoogle.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.loginGoogle.matchRejected, storeErrors)
      // .addMatcher(authApi.endpoints.loginWithCode.matchFulfilled, login)
      .addMatcher(authApi.endpoints.loginWithCode.matchRejected, storeErrors)
      .addMatcher(authApi.endpoints.register.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.register.matchRejected, storeErrors)
      .addMatcher(authApi.endpoints.sendCode.matchFulfilled, login)
      .addMatcher(authApi.endpoints.sendCode.matchRejected, storeErrors)
      .addMatcher(authApi.endpoints.authWithoutLogin.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.authWithoutLogin.matchFulfilled,
        storeTokens
      )
      .addMatcher(
        authApi.endpoints.sendCodeWithoutLogin.matchRejected,
        storeErrors
      )
      .addMatcher(
        authApi.endpoints.sendCodeWithoutLogin.matchFulfilled,
        (store) => {
          store.error = null;
        }
      )
      .addMatcher(
        userApi.endpoints.getUser.matchFulfilled,
        (state, { payload }: { payload: IUserResponse }) => {
          storeUser(state, payload);
          state.isAuth = true;
        }
      )
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state, action) => {
        deleteCookie("refreshToken");
        deleteCookie("accessToken");
        state.user = initialUser;
        state.accessToken = "";
        state.refreshToken = "";
        state.error = action.payload.success ? null : action.payload.message;
        state.isAuth = false;
      })
      .addMatcher(
        userApi.endpoints.getUsers.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TGetAllTherapistUsersResponse }
        ) => {
          state.user.users = payload.TherapistUsers;
        }
      )
      .addMatcher(
        userApi.endpoints.getMainPageInfo.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TMainPageInfo }) => {
          state.therapistWithUsers = payload.therapistWithUsers;
          state.usersWithOutTherapist = payload.usersWithOutTherapist;
          state.groupedUsersWithoutTherapistByFakeCompanyId =
            groupByFakeCompanyId(payload.usersWithOutTherapist);
        }
      )
      .addMatcher(
        userApi.endpoints.getStatuses.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TGetStatusesResponse }) => {
          state.statuses = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getMedias.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TMediaResponse }) => {
          state.medias = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getMigzars.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TMigzarResponse }) => {
          state.migzars = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getGenders.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TGenderResponse }) => {
          state.genders = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.sendUserEnteredSMSToTherapist.matchFulfilled,
        (state: IUserAuth, { payload }) => {
          state.showUserJoinedModal = payload.usersId;
        }
      )
      .addMatcher(
        userApi.endpoints.getAllAppointments.matchFulfilled,
        (state: IUserAuth, { payload }) => {
          // Handle new response format with appointments array
          let appointmentsArray: any[] = [];
          
          if (Array.isArray(payload)) {
            // Old format - direct array (for backward compatibility)
            appointmentsArray = payload;
          } else if (payload && 'appointments' in payload) {
            // New format - object with appointments array and googleEvents array
            const appointments = Array.isArray(payload.appointments) ? payload.appointments : [];
            const googleEvents = Array.isArray(payload.googleEvents) ? payload.googleEvents : [];
            
            // Merge both appointments and Google Calendar events
            appointmentsArray = [...appointments, ...googleEvents];
          } else {
            // console.error('getAllAppointments payload format unexpected:', payload);
            appointmentsArray = [];
          }

          state.calendarAppointments = appointmentsArray.map((item: any) => {
            // Handle different date formats for Google Calendar events
            let appointmentDate;
            
            if (item.appointmentDate) {
              appointmentDate = new Date(item.appointmentDate);
            } else if (item.start) {
              // Google Calendar events might use 'start' field
              appointmentDate = new Date(item.start);
            } else if (item.startDateTime) {
              // Or 'startDateTime' field
              appointmentDate = new Date(item.startDateTime);
            } else {
              appointmentDate = new Date();
            }
            
            return {
              ...item,
              appointmentDate: appointmentDate,
              startTime: item.startTime?.split(":").slice(0, 2).join(":"),
              endTime: item.endTime?.split(":").slice(0, 2).join(":"),
              isReport: item.isReport,
            };
          });
        }
      )
      .addMatcher(
        userApi.endpoints.getAllAppointments.matchRejected,
        (state: IUserAuth, { error }) => {
          // Log error but don't clear appointments if it's a rate limit error
          const errorMessage = (error as any)?.message || 'Unknown error';
          if (errorMessage.includes('429') || errorMessage.includes('Too Many Requests')) {
            // console.warn/=('Rate limit reached for appointments API. Keeping existing data.');
            // Don't clear existing appointments on rate limit
          } else {
            // console.error('getAllAppointments failed:', error);
            state.calendarAppointments = [];
          }
        }
      )
      .addMatcher(
        userApi.endpoints.postTherapistAvailability.matchFulfilled,
        (state: IUserAuth, { payload }) => {
          state.user.availability = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getTherapistAvailability.matchFulfilled,
        (state: IUserAuth, { payload }) => {
          state.user.availability = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getAvailableOpenTreatmentPlansByTherapistGuid.matchFulfilled,
        (state: IUserAuth, { payload }) => {
          state.allTreatmentPlan = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getAllOpenTreatmentPlans.matchFulfilled,
        (state: IUserAuth, { payload }) => {
          state.allTreatmentPlan = payload.filter(
            (t) =>
              !t.appointmentsProgram.therapistsGuid &&
              t.appointmentsProgram.statusId > 0 &&
              (!t.appointmentsProgram.roleId ||
                t.appointmentsProgram.roleId === state.user.roleId) &&
              (!t.appointmentsProgram.migzarId ||
                t.appointmentsProgram.migzarId === state.user.migzarId) &&
              (!t.appointmentsProgram.vetek ||
                t.appointmentsProgram.vetek <= (state.user?.vetek ?? 0)) &&
              (!t.appointmentsProgram.genderId ||
                t.appointmentsProgram.genderId === state.user.genderId) 
                        );

          state.user.treatmentPlan = payload.filter(
            (t) =>
              (t.appointmentsProgram.therapistsGuid === state.user.guid ||
              t.appointmentsProgram.therapistsGuid?.toUpperCase() ===
                state.user.guid.toUpperCase()) && t.appointmentsProgram.isApproved
          );
         
          // console.log("i am here:", state.user.treatmentPlan);
        }
      )
      .addMatcher(
        userApi.endpoints.getAllOpenTreatmentPlans.matchRejected,
        storeErrors
      )
      .addMatcher(
        userApi.endpoints.getAllMetaTables.matchFulfilled,
        (
          state: IUserAuth,
          action: PayloadAction<IAppointmentsProgramsMetaData>
        ) => {
          const appointmentsSequenceWithLabels =
            action.payload.AppointmentsSequence.map((sequence) => {
              const period = action.payload.Periods.find(
                (p) => p.Id === sequence.PeriodsId
              );
              const periodName = period?.Name || "";
              const count = sequence.CountInPeriod ?? 0;
              const label = t(
                count === 1
                  ? "APPOINTMENT_SEQUENCE_ONCE_PER_PERIOD"
                  : count === 2
                  ? "APPOINTMENT_SEQUENCE_TWICE_PER_PERIOD"
                  : "APPOINTMENT_SEQUENCE_N_TIMES_PER_PERIOD",
                { count, period: periodName }
              );
              return {
                ...sequence,
                value: sequence.Id,
                label,
              };
            });
          return {
            ...state,
            appointmentsProgramsData: {
              ...state.appointmentsProgramsData,
              AppointmentsDurations:
                action.payload?.AppointmentsDurations.filter(
                  (d) => d.Minutes !== null
                ).map((d) => ({
                  ...d,
                  value: d.Id,
                  label: d.Minutes || "No duration",
                })),
              AppointmentsSequence: appointmentsSequenceWithLabels,
              Medias: action.payload?.Medias.map((m) => ({
                ...m,
                value: m.Id,
                label: m.Name || "",
              })),
              Roles: action.payload?.Roles.map((r) => ({
                ...r,
                value: r.Id,
                label: r.Name,
              })).concat([
                { Id: 0, value: 0, Name: t('UNAFFILIATED'), label: t('UNAFFILIATED') },
              ]),
              Genders: action.payload?.Genders.map((g) => ({
                ...g,
                value: g.Id,
                label: g.Name,
              })).concat([
                { Id: 0, value: 0, Name: t('UNAFFILIATED'), label: t('UNAFFILIATED') },
              ]),
              Migzars: action.payload?.Migzars.map((m) => ({
                ...m,
                value: m.Id,
                label: m.Name,
              })).concat([
                { Id: 0, value: 0, Name: t('UNAFFILIATED'), label: t('UNAFFILIATED') },
              ]),
              TherapistsSpecializations: action.payload?.TherapistsSpecializations?.map((s: TNamedItem) => ({
                ...s,
                value: s.Id,
                label: s.Name,
              })) || [],
              FundingSources: action.payload?.FundingSources?.map((f: TNamedItem) => ({
                ...f,
                value: f.Id,
                label: f.Name,
              })) || [],
              TreatmentTypes: action.payload?.TreatmentTypes?.map((t: TNamedItem) => ({
                ...t,
                value: t.Id,
                label: t.Name,
              })) || [],
              AppointmentsProgramsStatus:
                action.payload.AppointmentsProgramsStatus?.map((s) => ({
                  ...s,
                  value: s.Id,
                  label: s.Desc,
                })),
                TreatmentRoomsList: action.payload?.TreatmentRoomsList?.map((r: TTreatmentRoom) => ({
                  ...r,
                  value: r.Id,
                  label: `${r.Name}${r.Capacity ? ` (${r.Capacity})` : ''}${r.Location ? ` - ${r.Location}` : ''}`,
                })) || [],
                CompanyServicesList: action.payload?.CompanyServicesList?.map((s: TCompanyService) => ({
                  ...s,
                  value: s.Id,
                  label: s.Name,
                })) || [],
            },
          };
        }
      )
      .addMatcher(
        userApi.endpoints.getRRMList.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: TRRMListEntry[] }) => {
          state.RRMList = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.getQuestionnaiersButtons.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TQuestionnaireButton[] }
        ) => {
          state.questionnairWithoutUserButtons = payload.filter(
            (button) => !button.isUsers
          );
          state.questionnairWithUserButtons = payload.filter(
            (button) => button.isUsers
          );
          state.userQuestionnairSiteLink = payload[0]?.siteLink ?? null;
        }
      )
      .addMatcher(
        userApi.endpoints.getUnSeenAppointmentsProgram.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TUnSeenAppointmentsProgram }
        ) => {
          state.unSeenAppointmentsPrograms = payload?.countOfNotRead ?? 0;
        }
      )
      .addMatcher(
        userApi.endpoints.updateUnSeenAppointmentsProgram.matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TUnSeenAppointmentsProgram }
        ) => {
          state.unSeenAppointmentsPrograms = payload?.countOfNotRead ?? 0;
        }
      )
      .addMatcher(
        userApi.endpoints.getPendingReports.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: IPendingReportResponce }) => {
          state.pendingReportAppointments = payload.pendingAppointments;
          state.pendingReportInquiries = payload.notReported;
          state.totalPendingReports = payload.notReportedTotal + payload.pendingAppointmentsTotal;
        }
      )
      .addMatcher(
        userApi.endpoints.therapistsSpecializationsDML.matchFulfilled,

        (state: IUserAuth, { payload }: { payload: TSpecialization[] }) => {
          state.user.allSpecializations = payload;
        }
      )
      .addMatcher(
        userApi.endpoints.therapistsSpecializations2TherapistsDML
          .matchFulfilled,
        (
          state: IUserAuth,
          { payload }: { payload: TTherapistSpecialization[] }
        ) => {
          state.user.therapistSpecializations = payload;
        }
      )
      .addMatcher(
        authApi.endpoints.SwitchCompanyUser.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: { accessToken: string; success: boolean } }) => {
          if (payload.accessToken) {
            state.accessToken = payload.accessToken;
            deleteCookie("accessToken");
            setCookie("accessToken", payload.accessToken, {
              expires: (() => {
                const currentTime = new Date();
                const nextDay12AM = new Date(currentTime);
                nextDay12AM.setDate(currentTime.getDate() + 1);
                nextDay12AM.setHours(0, 0, 0, 0);
                return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
              })(),
            });
          }
        }
      )
      .addMatcher(
        userApi.endpoints.getCompanyById.matchFulfilled,
        (state: IUserAuth, { payload }: { payload: ICompanyDetails }) => {
          state.isChargingCustomers = payload.isChargingCustomers;
          state.therapistsCanSeePrices = payload.therapistsCanSeePrices || false;
          state.canAddDefaultReports = payload.canAddDefaultReports || false;
          //state.isChargingCustomers = true; //for tests
        }
      );
    // .addMatcher(userApi.endpoints.updateUser.matchRejected, storeErrors);
  },
});

export const { actions, reducer } = userSlice;
export const { setShowUserJoinedModal, setUserAvatar } = userSlice.actions;
export default userSlice.reducer;
export const selectUser = (state: RootState) => state.user;
